import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { AUTH_PRIMARY_BUTTON_CLASS, AuthScreen } from '../components/AuthScreen'
import { AnchorButton } from '../components/ui/Button'
import { AUTH_FAILED_PARAM, AUTH_FAILED_VALUE, COGNITO_LOGIN_PATH, REAUTH_PARAM } from '../lib/routes'

/** True when Login is reached after a failed sign-in (MS-993):
 * OAuthCallback sends the browser to `/login?error=auth`. Anyone can type
 * the parameter, which is harmless: it only shows a fixed, translated line.
 * Read from window.location because Login also renders outside the router,
 * straight from App's Gate. */
function cameBackFromFailedSignIn(): boolean {
  return new URLSearchParams(globalThis.location.search).get(AUTH_FAILED_PARAM) === AUTH_FAILED_VALUE
}

/** True on `/login?reauth=1` (MS-997 G5): the daily sign-in fell due, or
 * Cognito refused the refresh token. Kept in the address (unlike `?error=auth`), so a
 * reload still asks the hosted page for the password. */
function cameBackForReauth(): boolean {
  return new URLSearchParams(globalThis.location.search).get(REAUTH_PARAM) === '1'
}

/** Drops `?error=auth` from the address once it has been read, so a reload
 * or Back after a new attempt does not show a failure that did not happen
 * (the prototype's setLoginStep() hides the error on every step). */
function forgetFailedSignIn() {
  const url = new URL(globalThis.location.href)
  url.searchParams.delete(AUTH_FAILED_PARAM)
  globalThis.history.replaceState(globalThis.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

/** The prototype's Authenticate-with-Mambu icon (`#login-mambu-btn`). */
function SignInIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  )
}

/** Which notice the danger pill shows, if any. "Sign in again" (MS-997 G5)
 * outranks the plain session-expired one: the session did not just lapse,
 * the password is needed. */
function loginBadgeKey(
  reauth: boolean,
  expired: boolean,
  loggedOut: boolean
): 'auth.reauthRequired' | 'login.sessionExpired' | 'login.signedOut' | null {
  if (reauth) return 'auth.reauthRequired'
  if (expired) return 'login.sessionExpired'
  return loggedOut ? 'login.signedOut' : null
}

export function Login() {
  const { t } = useTranslation()
  const { didLogOut, didExpire } = useAuth()
  const [authFailed] = useState(cameBackFromFailedSignIn)
  const [reauth] = useState(cameBackForReauth)
  useEffect(() => {
    if (authFailed) forgetFailedSignIn()
  }, [authFailed])
  const badgeKey = loginBadgeKey(reauth, didExpire, didLogOut)

  return (
    <AuthScreen
      // "You have been signed out", "Your session expired" and "Sign in
      // again" all use the same danger-pill badge as AccessDenied's "Access
      // denied" (see AuthScreen) — matches the prototype's shared
      // `.login-signed-out` class exactly, and Agent Studio's own
      // expired-session pill. The subtitle is unchanged from the default in
      // these states (the prototype's doLogout() only toggles the badge,
      // never rewrites the subtitle) — unlike AccessDenied, which does
      // overwrite it.
      badgeLabel={badgeKey ? t(badgeKey) : undefined}
      subtitle={t('login.cognitoSubtitle')}
      error={authFailed ? t('login.authFailed') : undefined}
    >
      {/* Step 1 of the prototype's landing card (ARCHITECTURE_DECISIONS.md
          §52, §54, §57) — "Authenticate with Mambu", the same button as
          Agent Studio's login. A full-page navigation to the backend, which
          redirects to the managed login; the account is chosen after
          sign-in (SelectAccount). No credential fields, and the identity
          provider is never named. After `?reauth=1` it asks the hosted page
          for the password again (G6). */}
      <AnchorButton
        href={reauth ? `${COGNITO_LOGIN_PATH}?${REAUTH_PARAM}=1` : COGNITO_LOGIN_PATH}
        className={AUTH_PRIMARY_BUTTON_CLASS}
      >
        <SignInIcon />
        <span>{t('login.authenticate')}</span>
      </AnchorButton>
    </AuthScreen>
  )
}
